import { parseArrivalWindowMinutes } from "./serviceOperations";
import {
  addSingaporeCalendarDays,
  formatSingaporeDate,
  getSingaporeDateKeyFromInstant,
  getSingaporeMinutesOfDay,
  singaporeTodayDateKey,
} from "./timezone";

export const SITE_VISIT_LIST_LIMITS = {
  today: 200,
  upcoming: 120,
  past: 40,
  pastMax: 400,
  search: 40,
} as const;

export type SiteVisitCalendarBucket = "today" | "upcoming" | "past";

export type SiteVisitListSchedule = {
  visitDate: string | Date;
  arrivalWindow?: string | null;
};

export type OperationalSiteVisitRow = SiteVisitListSchedule & {
  id: number;
  visitNumber: string;
  status: string;
  visitType: string;
  arrivalWindow: string | null;
  technicianName: string | null;
  picName: string | null;
  billable: boolean;
  waiverRequested: boolean;
  waiverApproved: boolean | null;
  followUpRequired: boolean;
  followUpNotes: string | null;
  custWhatsappStatus: string | null;
  createdBy: string | null;
  isCustomerUnmatched: boolean;
  customerName: string;
  customerAddress: string | null;
  arrivedAt: string | null;
  departedAt: string | null;
  cleoOnSite: boolean;
};

/** Singapore calendar day of the scheduled visit. Appointment time does not move the day. */
export function siteVisitCalendarBucket(
  visitDate: string | Date | null | undefined,
  now: Date,
): SiteVisitCalendarBucket {
  const key = getSingaporeDateKeyFromInstant(visitDate);
  const today = singaporeTodayDateKey(now);
  if (!key || key < today) return "past";
  if (key === today) return "today";
  return "upcoming";
}

/** Start of the arrival window, otherwise the scheduled instant's Singapore time of day. */
export function todayAppointmentSortMinutes(visit: SiteVisitListSchedule): number {
  const windowMinutes = parseArrivalWindowMinutes(visit.arrivalWindow);
  if (windowMinutes) return windowMinutes[0];
  return getSingaporeMinutesOfDay(visit.visitDate) ?? 24 * 60;
}

export function sortTodaySiteVisits<T extends SiteVisitListSchedule>(visits: T[]): T[] {
  return [...visits].sort((a, b) => todayAppointmentSortMinutes(a) - todayAppointmentSortMinutes(b) || compareInstant(a.visitDate, b.visitDate));
}

export type SiteVisitDateGroup<T> = {
  dateKey: string;
  label: string;
  visits: T[];
};

export function siteVisitGroupLabel(dateKey: string, now: Date): string {
  const today = singaporeTodayDateKey(now);
  const tomorrow = addSingaporeCalendarDays(today, 1);
  const dated = formatSingaporeDate(dateKey, { weekday: "long", day: "numeric", month: "short" }, "en-SG");
  if (dateKey === today) return `Today · ${dated}`;
  if (dateKey === tomorrow) return `Tomorrow · ${dated}`;
  return dated;
}

export function presentUpcomingSiteVisits<T extends SiteVisitListSchedule>(
  visits: T[],
  now: Date,
): SiteVisitDateGroup<T>[] {
  const sorted = [...visits].sort((a, b) => {
    const day = (getSingaporeDateKeyFromInstant(a.visitDate) ?? "").localeCompare(getSingaporeDateKeyFromInstant(b.visitDate) ?? "");
    if (day !== 0) return day;
    return todayAppointmentSortMinutes(a) - todayAppointmentSortMinutes(b) || compareInstant(a.visitDate, b.visitDate);
  });
  return groupInOrder(sorted, now);
}

export function presentPastSiteVisits<T extends SiteVisitListSchedule>(
  visits: T[],
  now: Date,
): SiteVisitDateGroup<T>[] {
  const sorted = [...visits].sort((a, b) => compareInstant(b.visitDate, a.visitDate));
  return groupInOrder(sorted, now);
}

export function siteVisitSearchWhen(visitDate: string | Date | null | undefined, now: Date): {
  bucket: SiteVisitCalendarBucket;
  label: string;
} {
  const bucket = siteVisitCalendarBucket(visitDate, now);
  const key = getSingaporeDateKeyFromInstant(visitDate);
  const label = key ? siteVisitGroupLabel(key, now) : "Date not set";
  return { bucket, label };
}

function groupInOrder<T extends SiteVisitListSchedule>(visits: T[], now: Date): SiteVisitDateGroup<T>[] {
  const groups: SiteVisitDateGroup<T>[] = [];
  for (const visit of visits) {
    const dateKey = getSingaporeDateKeyFromInstant(visit.visitDate) ?? "unknown";
    const current = groups[groups.length - 1];
    if (current?.dateKey === dateKey) current.visits.push(visit);
    else groups.push({ dateKey, label: dateKey === "unknown" ? "Date not set" : siteVisitGroupLabel(dateKey, now), visits: [visit] });
  }
  return groups;
}

function compareInstant(a: string | Date, b: string | Date): number {
  return new Date(a).getTime() - new Date(b).getTime();
}
